// One dependency between two legs (groups) of ONE plan — the row shape of
// `plan_group_edges` (`@developerz/db` `schema/plans.ts`), plan 101-multi-repo-goal-legs D2.
//
// An edge says WHEN its upstream releases the dependent (`release`) and WHAT the
// dependent waits for (`hold`):
//   - `release: merged`   — the upstream's PR merged (today's `planGroupSatisfiedSql`).
//   - `release: deployed` — merged AND the merge commit's newest verdict in `environment`
//                           is `success` (D3). `environment` is non-null exactly here.
//   - `hold: start`       — the dependent is not CLAIMED until the edge releases (today).
//   - `hold: merge`       — the dependent runs now; its MERGE waits for the release (D4).
//
// Stored vocabulary: these members are the `plan_group_edges_*_ck` CHECK lists. Adding one
// is a two-release change (`docs/standards/stored-closed-sets.md`).
//
// SLICE 06 (PR 4): the `deployed` release has its gate (`upstreamEdgeUnreleasedSql`,
// `@developerz/db` `queries/tasks/edge-release.ts`), so triage may state a `deployed` edge and
// the writer stores it as stated: the plan-group writers take {@link PlanEdgeInput}, an edge
// object or the bare id shorthand, normalized by {@link planEdgesOf}, ONE mapping.
//
// SLICE 07 (PR 5): the `merge` hold has its gate. The claim reads only `hold: start` edges;
// the merge egress (`@developerz/review` `guardedMerge`) refuses `upstream_leg_unreleased`
// while a `hold: merge` edge has not released, reading {@link UpstreamLegRelease}.

import { z } from 'zod';

export const PlanEdgeRelease = { Merged: 'merged', Deployed: 'deployed' } as const;
export const PlanEdgeReleaseSchema = z.enum(PlanEdgeRelease);
export type PlanEdgeRelease = z.infer<typeof PlanEdgeReleaseSchema>;

export const PlanEdgeHold = { Start: 'start', Merge: 'merge' } as const;
export const PlanEdgeHoldSchema = z.enum(PlanEdgeHold);
export type PlanEdgeHold = z.infer<typeof PlanEdgeHoldSchema>;

/** One edge into a group: `upstream` is a sibling group id of the same plan. */
export const PlanEdgeSchema = z
  .object({
    upstream: z.string().min(1),
    release: PlanEdgeReleaseSchema,
    environment: z.string().min(1).nullable(),
    hold: PlanEdgeHoldSchema,
  })
  // The `plan_group_edges_environment_ck` CHECK, stated in TS: `deployed` names the
  // environment it waits on, `merged` names none.
  .refine((e) => (e.release === PlanEdgeRelease.Deployed) === (e.environment !== null), {
    path: ['environment'],
    message: 'an edge names an environment exactly when release is deployed',
  });
export type PlanEdge = z.infer<typeof PlanEdgeSchema>;

/**
 * What a plan-group writer is handed per dependency: a stated {@link PlanEdge}, or a bare
 * upstream group id. The bare id is a SHORTHAND with exactly one meaning, `merged` / `start`,
 * the gate a sibling id in the retired `plan_groups.depends_on` always meant. It is not a second path: both forms become
 * the same row through {@link planEdgesOf}, and the stored row is the only thing a gate reads.
 *
 * Why the shorthand outlived slice 05: the plan-file lane, `plan.execute`'s revise, regroup,
 * unlink, the onboarding / scaffold one-group plans and the scout `plan_authored` wire (a
 * BOX-written wire: its object form ships reader-first, after this release) all speak ids and
 * have no edge vocabulary. For them a sibling id has only ever meant `merged` / `start`.
 * Triage states objects (`raw-task-triage.ts`), the one producer of another kind.
 */
export type PlanEdgeInput = string | PlanEdge;

/**
 * The stored edges of one group, from its writer's input: a bare id becomes `merged` / no
 * environment / `start`; a stated edge is parsed through {@link PlanEdgeSchema} and kept. A
 * repeated identical edge collapses to one, since the table's key is
 * `(plan_id, group_id, upstream_group_id)`. Two DIFFERENT edges into one upstream THROW: the
 * key holds one, and keeping either would be a guess about which gate the producer meant.
 */
export function planEdgesOf(input: readonly PlanEdgeInput[]): PlanEdge[] {
  const byUpstream = new Map<string, PlanEdge>();
  for (const item of input) {
    const edge = typeof item === 'string' ? shorthandEdge(item) : parseStatedEdge(item);
    const prior = byUpstream.get(edge.upstream);
    if (prior !== undefined && !sameEdge(prior, edge)) {
      throw new Error(
        `planEdgesOf: two different edges into '${edge.upstream}' (${JSON.stringify(prior)} and ` +
          `${JSON.stringify(edge)}). The table keys one edge per upstream: state one at the producer.`,
      );
    }
    byUpstream.set(edge.upstream, edge);
  }
  return [...byUpstream.values()];
}

/** The upstream ids of a writer's input, in input order, each once (the `plan.recorded` audit facts). */
export function planEdgeUpstreams(input: readonly PlanEdgeInput[]): string[] {
  return planEdgesOf(input).map((e) => e.upstream);
}

function shorthandEdge(upstream: string): PlanEdge {
  return { upstream, release: PlanEdgeRelease.Merged, environment: null, hold: PlanEdgeHold.Start };
}

function parseStatedEdge(edge: PlanEdge): PlanEdge {
  const parsed = PlanEdgeSchema.safeParse(edge);
  if (!parsed.success) {
    throw new Error(
      `planEdgesOf: the edge into '${edge.upstream}' is not a PlanEdge ` +
        `(${parsed.error.issues.map((i) => i.message).join('; ')}). Grade it at the producer.`,
    );
  }
  return parsed.data;
}

function sameEdge(a: PlanEdge, b: PlanEdge): boolean {
  return a.release === b.release && a.environment === b.environment && a.hold === b.hold;
}

/**
 * One `hold: merge` edge whose upstream has NOT released (slice 07, D4): what the merge gate's
 * `upstream_leg_unreleased` refusal names. Read by `@developerz/db` `readUnreleasedMergeEdges`.
 */
export type UnreleasedMergeEdge = {
  readonly planId: string;
  /** The dependent group: the leg whose PR the gate is asked to merge. */
  readonly groupId: string;
  readonly upstreamGroupId: string;
  /** The upstream leg's repo; `null` when the plan never wrote that group (no upstream FK). */
  readonly upstreamRepo: string | null;
  readonly release: PlanEdgeRelease;
  readonly environment: string | null;
};

/**
 * May this PR's leg merge, as far as its plan's `hold: merge` edges go? `released` covers a PR
 * that is no leg at all, and a leg whose every `merge` edge released. A discriminated answer,
 * never an empty list standing in for "released".
 */
export type UpstreamLegRelease =
  | { readonly kind: 'released' }
  | { readonly kind: 'held'; readonly edges: readonly UnreleasedMergeEdge[] };
